'use client';
import { useAriaLabels } from '@/hooks/useAriaLabels';

import { LinkButton } from '@/components/ui';
import type {
  CMSStoriesBanner as CMSStoriesBannerType,
  CMSCityPair,
  CMSStorySlide,
} from '@/lib/cms/types';
import type { CityPairItem, StoriesBannerFragment, StoryCardItem } from '@/lib/contentful/types';
import { getImageAlt, getImageUrl } from '@/lib/contentful/utils';
import clsx from 'clsx';
import Image from 'next/image';
import { Link } from '@/lib/i18n/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './StoriesBanner.module.scss';

export interface StoriesBannerProps {
  /** StoriesBanner data (supports both raw Contentful and adapted CMS formats) */
  data: StoriesBannerFragment | CMSStoriesBannerType;
  /** Optional className */
  className?: string;
  /** CMS index for unique data-testid generation */
  cmsIndex?: number;
}

/**
 * Default city pairs for the world clock crown (used if not provided from Contentful)
 */
const DEFAULT_CITY_PAIRS: CityPairItem[] = [
  { city12: 'London', city24: 'Wellington', rotation: 0 },
  { city12: 'Geneva', city24: 'Samoa', rotation: 30 },
  { city12: 'Cape Town', city24: 'Honolulu', rotation: 60 },
  { city12: 'Nairobi', city24: 'Anchorage', rotation: 90 },
  { city12: 'Dubai', city24: 'San Francisco', rotation: 120 },
  { city12: 'Calgary', city24: 'New Delhi', rotation: 150 },
  { city12: 'Thimphu', city24: 'Mexico', rotation: 180 },
  { city12: 'Angkor Wat', city24: 'Lima', rotation: 210 },
  { city12: 'Beijing', city24: 'Barbados', rotation: 240 },
  { city12: 'Tokyo', city24: 'Ushuaia', rotation: 270 },
  { city12: 'Hobart', city24: 'S. Georgia', rotation: 300 },
  { city12: 'Noumea', city24: 'Azores', rotation: 330 },
];

/**
 * World Clock Ring SVG with rotating city crown
 */
function WorldClockRing({
  cityPairs = DEFAULT_CITY_PAIRS,
  rotation = 0,
  imageUrl,
  showOnlyTopCity = false,
}: {
  cityPairs?: CityPairItem[];
  rotation?: number;
  imageUrl?: string | null;
  showOnlyTopCity?: boolean;
}) {
  if (imageUrl) {
    return (
      <div className={styles.worldClock} style={{ transform: `rotate(${rotation}deg)` }}>
        <Image
          src={imageUrl}
          alt="World clock showing multiple time zones"
          fill
          className={styles.worldClockImage}
        />
      </div>
    );
  }

  // Calculate which city should be visible at top (the one that rotates TO the top position)
  // When ring rotates by X degrees, the city at position X moves to where 0 was
  // So we need the city whose rotation matches the NEGATIVE of current rotation (mod 360)
  const normalizedRotation = ((-rotation % 360) + 360) % 360;
  const topCityIndex = cityPairs.findIndex((pair) => {
    const pairRot = pair.rotation ?? 0;
    return Math.abs(pairRot - normalizedRotation) < 15; // Within 15 degrees tolerance
  });

  // SVG world clock with city crown - cities INSIDE the ring
  return (
    <div className={clsx(styles.worldClock, styles.worldClockLarge)}>
      <svg
        viewBox="0 0 800 800"
        className={styles.worldClockSvg}
        aria-hidden="true"
        style={{ transform: `rotate(${rotation}deg)` }}
      >
        {/* Main circle - thicker stroke */}
        <circle
          cx="400"
          cy="400"
          r="340"
          fill="none"
          stroke="#f07d13"
          strokeWidth="2"
          opacity="0.9"
        />

        {/* City crown - positioned INSIDE the ring */}
        <g className={styles.cityCrown}>
          {cityPairs.map((pair, index) => {
            const pairRotation = pair.rotation ?? index * 30;
            const angle = (pairRotation - 90) * (Math.PI / 180);
            // Position cities INSIDE the ring (radius smaller than circle)
            // Round to 2 decimal places to avoid hydration mismatch from floating-point precision
            const radius = 280;
            const x = Math.round((400 + radius * Math.cos(angle)) * 100) / 100;
            const y = Math.round((400 + radius * Math.sin(angle)) * 100) / 100;

            // Check if this city should be visible
            const isTopCity = index === topCityIndex;
            const shouldShow = !showOnlyTopCity || isTopCity;

            return (
              <g
                key={pair.sys?.id || index}
                transform={`translate(${x}, ${y}) rotate(${pairRotation})`}
                className={styles.cityPair}
                style={{
                  opacity: shouldShow ? 1 : 0,
                  transition: 'opacity 0.5s ease-in-out',
                }}
              >
                {/* Marker dot on the ring */}
                <circle cx="0" cy={-60} r="5" fill="#f07d13" />
                {/* City 12-hour (top) */}
                <text
                  x="0"
                  y="-12"
                  textAnchor="middle"
                  fill="#f07d13"
                  fontSize="12"
                  fontWeight="700"
                  style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}
                >
                  {pair.city12}
                </text>
                {/* Divider line */}
                <line
                  x1="-35"
                  y1="0"
                  x2="35"
                  y2="0"
                  stroke="#f07d13"
                  strokeWidth="1.5"
                  opacity="0.8"
                />
                {/* City 24-hour (bottom) */}
                <text
                  x="0"
                  y="20"
                  textAnchor="middle"
                  fill="#f07d13"
                  fontSize="12"
                  fontWeight="700"
                  style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}
                >
                  {pair.city24}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}

/**
 * Progress indicator bars for story slider
 */
function ProgressIndicator({
  total,
  current,
  duration,
  isPaused,
}: {
  total: number;
  current: number;
  duration: number;
  isPaused: boolean;
}) {
  return (
    <div className={styles.progressBars}>
      {Array.from({ length: total }).map((_, index) => (
        <div
          key={index}
          className={clsx(
            styles.progressBar,
            index < current && styles.completed,
            index === current && styles.active
          )}
        >
          {index === current && (
            <div
              className={styles.progressFill}
              style={{
                animationDuration: `${duration}ms`,
                animationPlayState: isPaused ? 'paused' : 'running',
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Single story slide component
 */
function StorySlide({ story, isActive }: { story: StoryCardItem; isActive: boolean }) {
  const bgUrl = getImageUrl(story.backgroundImage);
  const bgAlt = getImageAlt(story.backgroundImage);

  return (
    <div className={clsx(styles.storySlide, isActive && styles.activeSlide)}>
      {/* Background */}
      {bgUrl && (
        <Image
          src={bgUrl}
          alt={bgAlt}
          fill
          className={styles.storyBackground}
          sizes="(max-width: 768px) 100vw, 504px"
          priority={isActive}
        />
      )}
      {story.backgroundColor && !bgUrl && (
        <div
          className={styles.storyBackgroundColor}
          style={{ backgroundColor: story.backgroundColor }}
        />
      )}

      {/* Overlay */}
      <div className={styles.storyOverlay} />

      {/* Story Content */}
      <div className={styles.storyContent}>
        {story.title && <h3 className={styles.storyTitle}>{story.title}</h3>}
        <div className={styles.storyBottom}>
          {story.excerpt && <p className={styles.storyExcerpt}>{story.excerpt}</p>}
          {story.ctaText && <span className={styles.storyCta}>{story.ctaText}</span>}
        </div>
      </div>
    </div>
  );
}

/**
 * Stories banner with world clock graphic and story slider
 * Used for highlighting editorial content with a visual element
 */
export function StoriesBanner({ data, className, cmsIndex }: StoriesBannerProps) {
  const aria = useAriaLabels();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Helper to generate test IDs: e.g., "storiesbanner-0-title"
  const testIdPrefix = cmsIndex !== undefined ? `storiesbanner-${cmsIndex}` : undefined;
  const testId = (suffix: string) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : undefined);

  // Check if this is CMS adapted data or raw Contentful
  const rawData = data as StoriesBannerFragment;
  const cmsData = data as CMSStoriesBannerType;

  // Extract data with defaults (handle both formats)
  const layout = data.layout || 'desktop';
  const label = data.label || 'Gallet Stories';
  // CMS uses 'heading', raw Contentful uses 'headingText'
  const heading = cmsData.heading || rawData.headingText || 'A New Era Begins';
  const bodyText = data.bodyText;
  const ctaText = data.ctaText || 'Discover all stories';
  const ctaUrl = data.ctaUrl || '/stories';
  const autoslideDuration = data.autoslideDuration || 5000;
  const ringPosition = data.ringPosition || 'auto';

  // Get story slides - CMS uses 'stories', raw uses 'storySlidesCollection.items'
  const rawStorySlides = rawData.storySlidesCollection?.items || [];
  const cmsStorySlides = cmsData.stories || [];
  const storySlides: StoryCardItem[] = (
    rawStorySlides.length > 0
      ? rawStorySlides
      : cmsStorySlides.map((story: CMSStorySlide) => ({
          title: story.title,
          description: story.description,
          backgroundImage: story.media ? { image: { url: story.media.url } } : null,
          ctaUrl: story.cta?.url || null,
        }))
  ) as StoryCardItem[];

  // Handle featuredStory fallback
  if (storySlides.length === 0) {
    if (rawData.featuredStory) {
      storySlides.push(rawData.featuredStory);
    } else if (cmsData.featuredStory) {
      storySlides.push({
        title: cmsData.featuredStory.title,
        description: cmsData.featuredStory.description,
        backgroundImage: cmsData.featuredStory.media
          ? { image: { url: cmsData.featuredStory.media.url } }
          : null,
        ctaUrl: cmsData.featuredStory.cta?.url || null,
      } as StoryCardItem);
    }
  }

  // Get city pairs - CMS uses 'cities', raw uses 'cityPairsCollection.items'
  const rawCityPairs = rawData.cityPairsCollection?.items || [];
  const cmsCityPairs = cmsData.cities || [];
  const cityPairs: CityPairItem[] =
    rawCityPairs.length > 0
      ? rawCityPairs
      : cmsCityPairs.length > 0
        ? (cmsCityPairs.map((city: CMSCityPair) => ({
            city12: city.city12,
            city24: city.city24,
            rotation: city.rotation,
          })) as CityPairItem[])
        : DEFAULT_CITY_PAIRS;

  // Get images - CMS has url directly, raw uses getImageUrl
  const worldClockImageUrl = cmsData.worldClockImage?.url || getImageUrl(rawData.worldClockImage);
  const contentPanelBgUrl =
    cmsData.contentPanelBackgroundMedia?.url || getImageUrl(rawData.contentPanelBackgroundMedia);
  const contentPanelBgAlt =
    cmsData.contentPanelBackgroundMedia?.alt || getImageAlt(rawData.contentPanelBackgroundMedia);
  const contentPanelBgColor = data.contentPanelBackgroundColor;
  const totalSlides = storySlides.length;
  const hasStories = totalSlides > 0;

  // Calculate ring rotation based on current slide (30° per slide when auto)
  const ringRotation =
    ringPosition === 'auto' && totalSlides > 1
      ? (currentSlide * (360 / 12)) % 360 // Rotate by one city pair position per slide
      : 0;

  // Auto-slide logic
  const goToNextSlide = useCallback(() => {
    if (totalSlides <= 1) return;
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  // Start/stop auto-slide timer
  useEffect(() => {
    if (totalSlides <= 1 || isPaused) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setTimeout(goToNextSlide, autoslideDuration);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [currentSlide, totalSlides, autoslideDuration, isPaused, goToNextSlide]);

  // Handle pause on hover
  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);

  // Get current story for the link
  const currentStory = storySlides[currentSlide];
  const storyUrl = currentStory?.ctaUrl || '#';

  // Determine panel order based on layout
  const isReversed = layout === 'teaser' || layout === 'reversed';

  return (
    <section
      className={clsx(
        styles.section,
        isReversed && styles.reversed,
        !hasStories && styles.noStories,
        className
      )}
      data-testid={testId('section')}
    >
      {/* Content Panel (2/3 section) */}
      <div
        className={styles.contentPanel}
        style={{ backgroundColor: contentPanelBgColor || undefined }}
      >
        {/* Content panel background image */}
        {contentPanelBgUrl && (
          <Image
            src={contentPanelBgUrl}
            alt={contentPanelBgAlt}
            fill
            className={styles.contentPanelBackground}
            sizes="(max-width: 768px) 100vw, 66vw"
            priority
            data-testid={testId('media')}
          />
        )}

        {/* Overlay for better text readability */}
        <div className={styles.contentPanelOverlay} />

        {ringPosition !== 'hidden' && (
          <WorldClockRing
            cityPairs={cityPairs}
            rotation={ringRotation}
            imageUrl={worldClockImageUrl}
            showOnlyTopCity={data.showOnlyTopCity ?? false}
          />
        )}

        <div className={styles.contentPanelInner}>
          <span className={styles.contentLabel} data-testid={testId('label')}>
            {label}
          </span>
          <h2 className={styles.contentHeading} data-testid={testId('title')}>
            {heading}
          </h2>
          {bodyText && (
            <p className={styles.contentBody} data-testid={testId('body')}>
              {bodyText}
            </p>
          )}
          <LinkButton
            href={ctaUrl}
            variant="outline"
            className={styles.contentCta}
            data-testid={testId('cta')}
          >
            {ctaText}
          </LinkButton>
        </div>
      </div>

      {/* Story Slider Panel (1/3 section) */}
      {hasStories && (
        <Link
          href={storyUrl}
          className={styles.storyPanel}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          data-testid={testId('slides')}
        >
          {/* Progress Indicator */}
          <ProgressIndicator
            total={Math.min(totalSlides, 4)} // Max 4 visible bars
            current={currentSlide}
            duration={autoslideDuration}
            isPaused={isPaused}
          />

          {/* Story Slides */}
          <div className={styles.storySlides}>
            {storySlides.map((story, index) => (
              <StorySlide
                key={story.sys?.id || index}
                story={story}
                isActive={index === currentSlide}
              />
            ))}
          </div>

          {/* Slide navigation dots (if more than 1 slide) */}
          {totalSlides > 1 && (
            <div className={styles.slideNav} role="tablist" aria-label={aria.action('storySlides')}>
              {storySlides.map((_, index) => (
                <button
                  key={index}
                  role="tab"
                  aria-selected={index === currentSlide}
                  aria-label={`Go to slide ${index + 1}`}
                  className={clsx(
                    styles.slideNavDot,
                    index === currentSlide && styles.activeNavDot
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    goToSlide(index);
                  }}
                />
              ))}
            </div>
          )}
        </Link>
      )}
    </section>
  );
}

StoriesBanner.displayName = 'StoriesBanner';
